import {Component, DestroyRef, OnInit, computed, inject, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {GrantChangeNotifierService} from '../../_modules/company/access/grant-change-notifier.service';
import {GrantHttpService} from '../../_modules/company/access/grant-http.service';
import {SelfGrantSummary, roleLabel, selfGrantTargetLabel} from '../../_modules/company/access/grant.models';
import {GlobalAlertService} from '../../_services/global-alert.service';
import {AlertLevel} from '../../enums/alert-level';

/**
 * **"Ihre freigeschalteten Zugänge"** — the other half of self-activation (#279's duration follow-up): a
 * platform owner's own list of what is currently unlocked for them in other tenants, and a way to give one
 * back before it expires on its own.
 *
 * ### Not scoped to self-grants alone
 *
 * `GET /grants/mine` (`MyRoleGrantsController`) answers for the caller's active grants full stop — self-
 * activated *and* invitation-redeemed alike, since "show me what I currently hold" is meaningful regardless of
 * how it was obtained, and the endpoint's bare `[Authorize]` reflects that. A row from an ordinary invitation
 * therefore carries no expiry at all ({@link SelfGrantSummary.expiresAt} `null`) — the copy below is written to
 * stay true for both, rather than claiming every row was self-issued.
 *
 * ### Why this exists beside the form that creates self-grants
 *
 * A self-grant is a privilege the operator handed *themself*, time-boxed by the duration slider precisely so it
 * does not linger unnoticed. But "it expires eventually" is not the same as "I can see what is currently open in
 * my name" — the picker showed the form to create one and nothing about what already exists, so an operator who
 * granted themselves `CompanyOwner` an hour ago and forgot has no way to check, let alone take it back early.
 * This panel is that visibility, read straight from the server rather than reconstructed from what the form last
 * submitted, because the form's state does not survive a page reload and the grant's does.
 *
 * ### Why revoke lives here rather than only being "let it expire"
 *
 * The duration slider bounds the worst case, not the common one — an operator who finishes early should be able
 * to close the door immediately rather than leave a live grant sitting around for the remaining hours "because
 * it will expire anyway". Revoking calls {@link GrantHttpService.revokeMyGrant}, the self-scoped sibling of the
 * admin-gated {@link GrantHttpService.revokeGrant} the grant-management screens use to withdraw someone else's
 * grant — this panel is not restricted to `PlatformOwner` sessions (a row here can equally be an invitation-
 * redeemed grant an ordinary employee holds), so it needs an endpoint that authorizes on "this is your own
 * grant" rather than on an administering role.
 */
@Component({
  selector: 'auth-my-grants-panel',
  standalone: true,
  host: {class: 'block'},
  template: `
    <section class="rounded-2xl border-[1px] border-gray-100 bg-white p-4 shadow-sm"
             aria-labelledby="my-grants-heading">
      <h2 id="my-grants-heading" class="text-base font-bold tracking-tight text-gray-900">
        Ihre freigeschalteten Zugänge
      </h2>
      <p class="mt-1 text-sm text-gray-500">
        Ihre aktiven Zugänge in anderen Firmen — durch Selbst-Freischaltung oder durch Einladung. Ein
        befristeter Zugang läuft automatisch ab; jeden Zugang können Sie hier auch vorzeitig zurückgeben.
      </p>

      @if (loading()) {
        <p class="mt-3 text-sm text-gray-500" role="status" aria-live="polite">Wird geladen…</p>
      } @else if (loadFailed()) {
        <p class="mt-3 rounded-xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-700" role="alert">
          Ihre freigeschalteten Zugänge konnten nicht geladen werden.
          <button type="button" (click)="load()" class="underline">Erneut versuchen</button>
        </p>
      } @else if (!grants().length) {
        <p class="mt-3 rounded-xl bg-gray-100 px-4 py-3 text-sm text-gray-600" data-test="my-grants-empty">
          Sie haben derzeit keine aktiven Zugänge in anderen Firmen.
        </p>
      } @else {
        <ul class="mt-3 space-y-2">
          @for (grant of grants(); track grant.id) {
            <li class="rounded-xl border-[1px] border-gray-100 p-3">
              <div class="flex flex-wrap items-center justify-between gap-2">
                <div class="min-w-0">
                  <span class="block truncate text-sm font-bold text-gray-900">{{ where(grant) }}</span>
                  <span class="mt-0.5 block text-xs text-gray-500" data-test="my-grants-remaining">
                    {{ remaining(grant) }}
                  </span>
                </div>
                @if (pending()?.id === grant.id) {
                  <span class="flex shrink-0 items-center gap-2">
                    <button type="button" (click)="pending.set(null)"
                            class="rounded-2xl border-[1px] border-gray-200 px-3 py-1.5 text-sm font-semibold
                                   text-gray-600 transition-all duration-200 hover:bg-gray-50 active:scale-95">
                      Abbrechen
                    </button>
                    <button type="button" (click)="confirmRevoke(grant)" [disabled]="busy() === grant.id"
                            class="rounded-2xl bg-red-600 px-3 py-1.5 text-sm font-semibold text-white shadow-lg
                                   transition-all duration-200 hover:bg-red-700 active:scale-95
                                   disabled:cursor-not-allowed disabled:opacity-30">
                      Wirklich widerrufen
                    </button>
                  </span>
                } @else {
                  <button type="button" (click)="pending.set(grant)" [disabled]="!!busy()"
                          [attr.aria-label]="'Zugang zu ' + where(grant) + ' widerrufen'"
                          class="shrink-0 rounded-2xl border-[1px] border-red-200 px-3 py-1.5 text-sm
                                 font-semibold text-red-700 transition-all duration-200 hover:bg-red-50
                                 active:scale-95 disabled:cursor-not-allowed disabled:opacity-30">
                    Widerrufen
                  </button>
                }
              </div>

              @if (grant.roleNames.length) {
                <span class="mt-2 flex flex-wrap gap-1">
                  @for (role of grant.roleNames; track role) {
                    <span class="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs
                                 font-semibold text-gray-600">{{ label(role) }}</span>
                  }
                </span>
              }
            </li>
          }
        </ul>
      }
    </section>
  `
})
export class MyGrantsPanelComponent implements OnInit {
  private readonly grants$ = inject(GrantHttpService);
  private readonly alerts = inject(GlobalAlertService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly grantChanges = inject(GrantChangeNotifierService);

  protected readonly grants = signal<SelfGrantSummary[]>([]);
  protected readonly loading = signal(true);
  protected readonly loadFailed = signal(false);

  /** The grant whose "really?" confirmation is on screen, or null. One at a time, by construction. */
  protected readonly pending = signal<SelfGrantSummary | null>(null);

  /** The id of the grant currently being revoked, or null — disables just that row's buttons, not every row. */
  protected readonly busy = signal<string | null>(null);

  /**
   * The clock the remaining-time labels read, ticking every 30 seconds — fine-grained enough for a live
   * countdown that is read in minutes, and cheap enough to run for as long as the panel is mounted.
   */
  private readonly now = signal(Date.now());

  protected readonly label = roleLabel;
  protected readonly where = selfGrantTargetLabel;

  ngOnInit(): void {
    this.load();

    const clock = setInterval(() => this.tick(), 30_000);
    this.destroyRef.onDestroy(() => clearInterval(clock));

    // Reacts to a grant change made anywhere else in the app — e.g. a self-grant issued from the bottom
    // switcher's panel while this panel is mounted alongside it (GrantChangeNotifierService). `load()` never
    // calls `notifyChanged()` itself, so this cannot cascade into a loop.
    this.grantChanges.changed$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.reload());
  }

  /**
   * Re-reads the clock the remaining-time labels are computed from. Its own method — rather than an inline
   * `setInterval` callback — so a test can drive the countdown deterministically (advance a mocked `Date.now`,
   * call this) instead of waiting on a real 30-second timer or reaching for `fakeAsync`.
   */
  protected tick(): void {
    this.now.set(Date.now());
  }

  /**
   * Reloads the list from the server. Public so the chooser can refresh it after a self-grant is issued in the
   * sibling panel — the grant is a new row here, and without this the panel would keep showing the pre-grant
   * list until the page is reopened.
   */
  public reload(): void {
    this.load();
  }

  protected load(): void {
    this.loading.set(true);
    this.loadFailed.set(false);

    this.grants$.listMine().subscribe({
      next: grants => {
        this.loading.set(false);
        this.grants.set(grants ?? []);
      },
      error: () => {
        this.loading.set(false);
        this.loadFailed.set(true);
      }
    });
  }

  /**
   * The remaining-time label for one grant: a fixed "läuft ab um HH:MM Uhr" plus a live "(noch …)" countdown
   * that recomputes every {@link now} tick — so a panel left open through the last few minutes of a grant
   * counts down rather than showing a clock face going stale.
   */
  protected remaining(grant: SelfGrantSummary): string {
    if (!grant.expiresAt) return 'Kein Ablaufdatum hinterlegt.';

    const expiry = new Date(grant.expiresAt).getTime();
    const diffMs = expiry - this.now();
    if (diffMs <= 0) return 'Läuft gerade ab.';

    const time = new Date(grant.expiresAt).toLocaleTimeString('de-DE', {hour: '2-digit', minute: '2-digit'});
    return `Läuft ab um ${time} Uhr (noch ${this.countdown(diffMs)})`;
  }

  /** "noch X" in the coarsest unit that stays readable — minutes below an hour, hours and minutes above it. */
  private countdown(diffMs: number): string {
    const totalMinutes = Math.max(1, Math.round(diffMs / 60_000));
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    if (hours === 0) return `${minutes} Min.`;
    if (minutes === 0) return `${hours} Std.`;
    return `${hours} Std. ${minutes} Min.`;
  }

  /** Opens the "really?" step for one row, closing whichever other row had it open. */
  protected confirmRevoke(grant: SelfGrantSummary): void {
    if (this.busy()) return;
    this.busy.set(grant.id);

    this.grants$.revokeMyGrant(grant.id).subscribe({
      next: () => {
        this.busy.set(null);
        this.pending.set(null);
        this.load();
        // Tells the switcher (and any other live view) to stop offering the tenant just given back — see
        // GrantChangeNotifierService's doc comment. This panel also hears its own signal via the subscription
        // above, so the row briefly reloads twice; harmless, and simpler than excluding the originator.
        this.grantChanges.notifyChanged();
      },
      error: () => {
        this.busy.set(null);
        this.pending.set(null);
        // No structured refusal worth branching on here — a stale row (already expired or already revoked
        // elsewhere) is by far the likely case, and reloading shows the true current list either way.
        this.alerts.createAndShow('Widerruf nicht möglich',
          'Der Zugang konnte nicht widerrufen werden. Bitte versuchen Sie es erneut.', AlertLevel.error, 8000);
        this.load();
      }
    });
  }
}
